import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import SelectController from "@/common/components/SelectController";
import useDebounce from "@/common/hooks/useDebounce";
import { Mapper } from "@/common/services/mapper";
import { EstablishmentCompanyData } from "@/common/types/establishment/company.type";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useUpdateEstablishmentCompany } from "../../hooks/useUpdateEstablishmentCompany";
import { updateEstablishmentCompanyDefaultValues } from "../../utils/updateEstablishmentCompanyDefaultValues";
import { useCompanies } from "@/features/companies/hooks/useCompanies";
import { CompaniesTransformer } from "@/features/companies/services/companiesTransformer";
import { useParams } from "react-router-dom";
import { updateEstablishmentCompanySchema, UpdateEstablishmentCompanyValidationSchema } from "../../schemas/updateEstablishmentCompany.schema";
import { EstablishmentAllowedClientCompanyData } from "../../types/establishmentAllowedClientCompanies.type";
import { Option } from "@/common/types/option";

interface UpdateEstablishmentDisplayAndSalesRestrictionModalProps {
  establishmentCompany?: EstablishmentCompanyData;
  establishmentAllowedClientCompanies?: EstablishmentAllowedClientCompanyData[]
  establishmentAllowedClientCompaniesOptions?: Option[]
  disabled?: boolean;
}

const UpdateEstablishmentDisplayAndSalesRestrictionModal = ({ 
  establishmentCompany,
  establishmentAllowedClientCompanies,
  establishmentAllowedClientCompaniesOptions,
  disabled,
 }: UpdateEstablishmentDisplayAndSalesRestrictionModalProps) => {
  const { id } = useParams();
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);

  const [searchCompanies, setSearchCompanies] = useDebounce('');

  const [messages, setMessages] = useState<string[] | boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  const { mutate: updateEstablishmentCompany, isPending: updateEstablishmentCompanyIsPending } = useUpdateEstablishmentCompany({
    establishmentCompanyId: establishmentCompany?.id,
    establishmentCompany,
    establishmentAllowedClientCompanies: establishmentAllowedClientCompanies,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ 'establishmentAllowedClientCompanies', id, ] })
      setIsOpen(false);
    },
    onError: (error) => {
      setIsOpen(false);
      setIsError(true);
      reset(updateEstablishmentCompanyDefaultValues(establishmentCompany))
      setMessages((error.response?.data as { errors: { base: string[]; }; })?.errors?.base || null);
    },
  });

  const { 
    data: companiesOptions, 
    isPending: companiesIsLoading
   } = useCompanies({
    filters: {
      search: searchCompanies
    },
    mapper: Mapper.mapWithTransformer({ transformer: CompaniesTransformer.optionsTransformer() }),
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UpdateEstablishmentCompanyValidationSchema>({
    resolver: zodResolver(updateEstablishmentCompanySchema),
    defaultValues: updateEstablishmentCompanyDefaultValues(establishmentCompany, establishmentAllowedClientCompanies),
  });

  const onSubmit: SubmitHandler<UpdateEstablishmentCompanyValidationSchema> = (data) => updateEstablishmentCompany(data);

  useEffect(() => {
    reset(updateEstablishmentCompanyDefaultValues(establishmentCompany));
  }, [isOpen]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        Trigger={
          <Button 
            operationKlassToCheckPermission="Admin::Establishment::Companies::Update"
            disabled={disabled}
          >
            Editar
          </Button>
        }
        triggerDisabled={disabled}
        useCancellableFooter
        showClose={false}
        title="Editar restrição de exibição e vendas"
        className="min-w-[720px]"
        bodyClassName="text-white-800"
        titleClass="text-2xl"
        cancelButtonClassName="bg-white-200 rounded-es-lg"
        confirmButtonClassName="bg-blue-700 rounded-ee-lg"
        disabled={updateEstablishmentCompanyIsPending}
        confirmButtonIsLoading={updateEstablishmentCompanyIsPending}
        onCancel={reset}
        onConfirm={handleSubmit(onSubmit)}
        body={(
          <form>
            <SelectController
              control={control}
              name="allowed_client_companies_attributes"
              label="Empresas permitidas"
              isMulti
              onInputChange={setSearchCompanies}
              defaultValue={establishmentAllowedClientCompaniesOptions}
              options={companiesOptions}
              isLoading={companiesIsLoading}
              isDisabled={companiesIsLoading}
              errorMessage={errors.allowed_client_companies_attributes?.message || undefined}
              operationKlassToCheckPermission="Admin::Establishment::Companies::AllowedClientCompanies"
            />
          </form>
        )}
      />

      <ModalMessageSimple
        messages={messages}
        setMessages={setMessages}
        isError={isError}
      />
    </>
  );
};

export default UpdateEstablishmentDisplayAndSalesRestrictionModal;
